import { Component, TemplateRef, ViewChild } from '@angular/core';
import { DialogBase, DialogCloseReason } from '../dialogs/base-dialog';
import { TranslateService } from '@ngx-translate/core';
import { LookupGridComponent, LookupRequestInfo } from './lookup-grid.component';
import { LookupDialogService } from './lookup-dialog.service';
import { ISimpleDropdownOption } from '../components/simple-dropdown/simple-dropdown.component';
import { CrmEntityLookupKnownSubType, CrmEntityTypeEnum } from '../crm-entities/crm-entities.model';
import { Logger } from '../monitoring/logger/logger';

export type EntitySubTypes = { [id in CrmEntityTypeEnum]?: CrmEntityLookupKnownSubType; } | { [id in CrmEntityTypeEnum]?: string; } | { [id: number]: CrmEntityLookupKnownSubType; } | { [id: number]: string; };

export interface ILookupEntityData {
  name: string;
  id: CrmEntityTypeEnum;
}

export interface BaseLookupEntity {
  Id: string;
  Name: string;
  CurrencyName: string;
  entityType: string;
}

export interface ILookupDialogResultData {
  fieldList: ILookupColumn[];
  selected: any[];
  entityType: CrmEntityTypeEnum;
  entityName: string;
}

export interface ILookupColumn {
  displayName: string;
  field: string;
  sortable: boolean;
  visible: boolean;
  width: string;
}

export interface ILookupView {
  FetchXML: string;
  Id: string;
  IsDefault: boolean;
  Name: string;
}

export interface ILookupDialogConfig {
  entityTypes?: CrmEntityTypeEnum[];
  entitySubTypes?: EntitySubTypes;
  singleSelection?: boolean;
  includeLookupViews?: boolean;
  editorType?: string;
}

@Component({
  selector: 'lookup-dialog',
  templateUrl: './lookup-dialog.html'
})

export class LookupDialogComponent extends DialogBase {
  @ViewChild('grid', {static: false}) gridComponent: LookupGridComponent;
  isLoading: boolean = true;
  entityTypes: ISimpleDropdownOption[] = [];
  entityType: CrmEntityTypeEnum;
  entitySubTypes?: { [id: number]: string; };
  entityName: string;
  entityViews: ISimpleDropdownOption[] = [];

  gridSelectionType: string;

  fieldList: ILookupColumn[] = [];
  selected: BaseLookupEntity[] = [];
  multiSelectedHash = {};
  multiSelectedHashIsEmpty = true;

  changedRequestInfoAttribute: string;

  requestInfo: LookupRequestInfo = {
    pageType: '',
    pageSize: 50,
    currentPageIndex: 1,
    entityViewId: '',
    filter: '',
    entityType: 0,
    includeLookupViews: true,
  };

  dialogConfig: ILookupDialogConfig;

  constructor(private lookupDialogService: LookupDialogService,
              private logger: Logger,
              private translate: TranslateService) {
    super();

  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  close() {
    super.close({ reason: DialogCloseReason.CANCEL });
  }

  add() {
    const lookupDialogResultData: ILookupDialogResultData = {
      selected: this.dialogConfig.singleSelection ? this.selected : Object.values(this.multiSelectedHash),
      fieldList: this.fieldList,
      entityType: this.entityType,
      entityName: this.entityName
    };
    super.close({ reason: DialogCloseReason.OK, data: lookupDialogResultData });
  }

  onViewIdChange(entityViewId: any) {
    this.requestInfo.entityViewId = entityViewId;
    this.requestInfo.currentPageIndex = 1;
    this.requestInfo.filter = '';
    this.changedRequestInfoAttribute = 'entityViewId';
    this.clearChangedRequestInfoAttribute();
    this.requestInfo = new LookupRequestInfo(this.requestInfo);
  }

  onEntityTypeChange(entityType: CrmEntityTypeEnum) {
    this.entityViews = [];
    this.selected = [];

    if (entityType) {
      this.entityType = <CrmEntityTypeEnum>Number(entityType);
      this.entityName = CrmEntityTypeEnum[this.entityType];
      this.lookupDialogService.entityType = this.entityType;
      this.requestInfo.entityType = entityType;
      if (this.entitySubTypes) {
        this.requestInfo.entitySubType = this.entitySubTypes[this.entityType];
      }
      this.onViewIdChange('');
    }

    this.loadEntityData();
  }

  clearChangedRequestInfoAttribute() {
    setTimeout(() => {
      this.changedRequestInfoAttribute = '';
    }, 1000);
  }

  newFilterSelected(filter: string) {
    this.requestInfo.filter = filter;
    this.changedRequestInfoAttribute = 'filter';
    this.clearChangedRequestInfoAttribute();
    this.requestInfo = new LookupRequestInfo(this.requestInfo);
  }

  setEntityData() {
    switch (this.entityType) {
      case CrmEntityTypeEnum.MarketingList:
        this.requestInfo.pageType = 'list';
        break;
      case CrmEntityTypeEnum.User:
        this.requestInfo.pageType = 'systemuser';
        break;
      case CrmEntityTypeEnum.Contact:
        this.requestInfo.pageType = 'contact';
        break;
      case CrmEntityTypeEnum.Lead:
        this.requestInfo.pageType = 'lead';
        break;
      case CrmEntityTypeEnum.Account:
        this.requestInfo.pageType = 'account';
        break;
      case CrmEntityTypeEnum.Team:
        this.requestInfo.pageType = 'team';
        break;
      case CrmEntityTypeEnum.Workflow:
        this.requestInfo.pageType = 'workflow';
        break;
      case CrmEntityTypeEnum.EmailTemplate:
        this.requestInfo.pageType = 'cdi_emailtemplate';
        break;
      case CrmEntityTypeEnum.EmailSend:
        this.requestInfo.pageType = 'cdi_emailsend';
        break;
      case CrmEntityTypeEnum.WebForm:
        this.requestInfo.pageType = 'cdi_webcontent';
        break;
      case CrmEntityTypeEnum.FormCapture:
        this.requestInfo.pageType = 'cdi_formcapture';
        break;
      case CrmEntityTypeEnum.FormField:
        this.requestInfo.pageType = 'cdi_formfield';
        break;
      case CrmEntityTypeEnum.SurveyQuestion:
        this.requestInfo.pageType = 'cdi_surveyquestion';
        break;
      case CrmEntityTypeEnum.Domain:
        this.requestInfo.pageType = 'cdi_domain';
        break;
      case CrmEntityTypeEnum.Campaign:
        this.requestInfo.pageType = 'campaign';
        break;
      case CrmEntityTypeEnum.EmailCName:
        this.requestInfo.pageType = 'cdi_emailcname';
        break;
      case CrmEntityTypeEnum.Currency:
        this.requestInfo.pageType = 'transactioncurrency';
        break;
      case CrmEntityTypeEnum.Event:
        this.requestInfo.pageType = 'cdi_event';
        break;
      case CrmEntityTypeEnum.ContactAttributes:
        this.requestInfo.pageType = 'contact_attributes';
        break;
      case CrmEntityTypeEnum.LeadAttributes:
        this.requestInfo.pageType = 'lead_attributes';
        break;
      case CrmEntityTypeEnum.SubscriptionList:
        this.requestInfo.pageType = 'cdi_subscriptionlist';
        break;
    }
  }

  loadEntityData() {
    this.setEntityData();
    this.getEntityViews();
  }

  initDialog() {
    this.dialogConfig = this.extraData as ILookupDialogConfig;

    const entityTypes: CrmEntityTypeEnum[] = this.dialogConfig.entityTypes ? this.dialogConfig.entityTypes : [CrmEntityTypeEnum.User];

    if (this.dialogConfig.entitySubTypes) {
      this.entitySubTypes = this.dialogConfig.entitySubTypes;
    }

    for (let i = 0; i < entityTypes.length; i++) {
      if (entityTypes[i]) {
        const entityType: ISimpleDropdownOption = {
          displayText: this.translate.instant(`COMMON.CRM_ENTITY_NAMES.${CrmEntityTypeEnum[entityTypes[i]]}`),
          value: entityTypes[i]
        };
        this.entityTypes.push(entityType);
      }

    }

    this.gridSelectionType = 'single';
    if (this.dialogConfig.singleSelection !== undefined && this.dialogConfig.singleSelection !== null) {
      this.gridSelectionType = this.dialogConfig.singleSelection ? 'single' : 'multiClick';
    } else {
      this.dialogConfig.singleSelection = true;
    }

    if (this.entityTypes && this.entityTypes.length > 0) {
      this.entityType = this.entityTypes[0].value;
      this.entityName = this.translate.instant(`COMMON.CRM_ENTITY_NAMES.${CrmEntityTypeEnum[this.entityType]}`);
      this.lookupDialogService.entityType = this.entityType;
      this.requestInfo.entityType = this.entityType;
      if (this.entitySubTypes) {
        this.requestInfo.entitySubType = this.entitySubTypes[this.entityType];
      }
      this.onViewIdChange('');
    }

    if (this.dialogConfig.editorType) {
      this.requestInfo.editorType = this.dialogConfig.editorType;
    }

    if (this.dialogConfig.includeLookupViews !== null && this.dialogConfig.includeLookupViews !== undefined) {
      this.requestInfo.includeLookupViews = this.dialogConfig.includeLookupViews;
    }

    this.loadEntityData();
  }

  getEntityViews() {
    this.lookupDialogService.getEntityViews(this.requestInfo)
      .subscribe((data) => this.handleEntityViewsResult(data),
        (err) => {
          this.handleEntityViewsError(err);
        });
  }

  handleEntityViewsResult(res: ILookupView[]) {
    if (res) {
      const entityViews = res;
      this.entityViews = entityViews.map((val: ILookupView) => {
        return { value: val.Id, displayText: val.Name };
      });
      for (let i = 0; i < entityViews.length; i++) {
        if (entityViews[i].IsDefault) {
          this.requestInfo.entityViewId = entityViews[i].Id;
          break;
        }
      }
      if ((!this.requestInfo.entityViewId || this.requestInfo.entityViewId.length === 0) && entityViews.length > 0) {
        this.requestInfo.entityViewId = entityViews[0].Id;
      }

      this.onViewIdChange(this.requestInfo.entityViewId);
    }
  }

  handleEntityViewsError(error: any) {
    this.logger.error(`error in get entity views from server: ${error.originalError || error}`);
  }

  selectedRecordsChanged(selected: any[]) {
    this.selected = selected;
  }

  columnsChanged(fieldList: ILookupColumn[]) {
    this.fieldList = fieldList;
  }

  onMultiSelectRemove(entityToRemove: BaseLookupEntity | any, event: MouseEvent) {
    event.stopPropagation();

    delete this.multiSelectedHash[entityToRemove.Id];
    if (Object.keys(this.multiSelectedHash).length === 0) {
      this.multiSelectedHashIsEmpty = true;
    }
  }

  onMultiSelectAdd() {
    this.selected.forEach((selectedIntity: BaseLookupEntity) => {
      this.multiSelectedHash[selectedIntity.Id] = selectedIntity;
      this.multiSelectedHashIsEmpty = false;
    });

    this.gridComponent.clearSelected();
  }

  shouldAddBeDisabled() {
    return this.dialogConfig && (
      (this.dialogConfig.singleSelection && this.selected.length === 0) ||
      (!this.dialogConfig.singleSelection && this.multiSelectedHashIsEmpty)
    );
  }
}
